import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { Service, SortOrderDirection } from 'webapp-types';
import { OrderDirection } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import type { SearchProps } from 'webapp/utils/filter';

import type { FilterSortSettings } from 'webapp/models-custom/filter-sort/base';
import FilterSort from 'webapp/models-custom/filter-sort/base';
import type { FilterOption } from 'webapp/models-custom/filter-sort/filter';
import type SourceType from 'webapp/models-custom/source-type';

export enum AvailableSourceSortBy {
	POPULARITY = 'Popularity',
	ALPHABETICALLY = 'Alphabetically',
}

export enum AvailableSourceType {
	PREBUILT_METRICS = 'prebuilt-metrics',
	API_METRIC_BUILDER = 'api-metric-builder',
	DATASET = 'dataset',
	CONNECTED = 'connected',
	NOT_CONNECTED = 'not-connected',
}

export default class FilterSortAvailableSource extends FilterSort<SourceType> {
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;

	@tracked override sortProp = 'popularity';
	@tracked override sortOrder: SortOrderDirection = OrderDirection.ASC;

	override searchProps: SearchProps = {
		name: 2,
		key: 1,
	};

	override saveFilterInUrlHash = true;

	override typedFilterEventName = TrackingEvent.DATASOURCE_SEARCH;

	override itemDomSelector = 'li.filter-sort-item';

	constructor(options: FilterSortSettings<SourceType>) {
		super(options);

		const selectedFilters = this.readSavedSelectedFilters();
		if (selectedFilters[3]) {
			const selected = selectedFilters[3][0] as AvailableSourceSortBy;

			this.onSortFilterChange(selected);
		}

		this.createFilter<string>({
			id: 3,
			label: 'Sort by',
			name: 'sort',
			stringValues: true,
			selected: selectedFilters[3] as string | undefined,
			isSort: true,
			getOptions: () => this.sortByOptions,
			onSelectedChange: (filter) => {
				const selected = filter.selected[0] as AvailableSourceSortBy;

				this.onSortFilterChange(selected);
			},
		});

		// Categories (used only)
		this.createFilter<string>({
			id: 1,
			label: 'Category',
			name: 'category',
			stringValues: true,
			selected: selectedFilters[1] as string | undefined,
			getOptions: () => {
				return this.categoryOptions;
			},
			filterFn: (item, selected) => {
				return selected.some((categoryId) => {
					return this.mmSourceTypesService.categoryFilterFunction(item, categoryId);
				});
			},
		});

		// Show (capability and connection state)
		this.createFilter<string>({
			id: 2,
			label: 'Show',
			name: 'show',
			stringValues: true,
			selected: selectedFilters[2] as string | undefined,
			getOptions: () => {
				return this.typeOptions;
			},
			filterFn: (item, selected) => {
				return selected.some((typeId) => {
					switch (typeId) {
						case AvailableSourceType.PREBUILT_METRICS:
							return item.hasBasicOrPredefinedMetrics && !item.isDb && !item.isSheet;
						case AvailableSourceType.API_METRIC_BUILDER:
							// The native metric builder, not custom API integrations
							return item.isQBSupported && !item.isDb && !item.isSheet;
						case AvailableSourceType.DATASET:
							return item.isDatasetSupported;
						case AvailableSourceType.CONNECTED:
							return item.isAlreadyConnected;
						case AvailableSourceType.NOT_CONNECTED:
							return !item.isAlreadyConnected;
						default:
							return false;
					}
				});
			},
		});
	}

	override get hasItems(): boolean {
		return true;
	}

	@cached
	get categoryOptions() {
		return this.mmSourceTypesService.getSourceTypeCategories();
	}

	get typeOptions(): FilterOption<string>[] {
		return [
			{ id: 'all', text: 'All' },
			{ id: AvailableSourceType.PREBUILT_METRICS, text: 'Prebuilt metrics available' },
			{
				id: AvailableSourceType.API_METRIC_BUILDER,
				text: 'API metric builder available',
			},
			{
				id: AvailableSourceType.DATASET,
				text: 'Datasets available',
				class: 'datasets',
				affixIcon: 'Table',
			},
			{ id: AvailableSourceType.CONNECTED, text: 'Already connected' },
			{ id: AvailableSourceType.NOT_CONNECTED, text: 'Not yet connected' },
		];
	}

	get sortByOptions(): FilterOption<string>[] {
		return Object.values(AvailableSourceSortBy).map((sort) => ({
			id: sort,
			text: sort,
		}));
	}

	// Can be overriden
	override updateSortBy(sortBy: AvailableSourceSortBy): void {
		switch (sortBy) {
			default:
			case AvailableSourceSortBy.POPULARITY:
				this.sortProp = 'popularity';
				break;
			case AvailableSourceSortBy.ALPHABETICALLY: {
				this.sortProp = 'name';
				break;
			}
		}
	}
}
